import type { ReactNode } from "react";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router";

import { FilterButton, InfinityLoader, MasterDetailLayout } from "common/components";
import { getPageId } from "common/utilities";
import { useDispatch, useSelector } from "common/utilities/react-redux";
import { useGo } from "common/utilities/useGo";
import { useTrack } from "common/utilities/useTrack";
import { useTranslate } from "common/utilities/useTranslate";
import { getSiteLanguage } from "domains/language/selectors";
import { activateModal } from "domains/modal/actions";
import { applyStakeholdersSearchCriteria } from "domains/stakeholders/actions";
import { StakeholderDetail } from "domains/stakeholders/components/StakeholderDetail";
import { StakeholderListItem } from "domains/stakeholders/components/StakeholderListItem";
import {
  createStakeholdersForTopicSelector,
  getIsStakeholdersFiltered,
  getStakeholderFilterCount,
  getStakeholdersSearchCriteria,
} from "domains/stakeholders/selectors";
import { getTopicPageEvent, getTopicRelatedItemDetailPageEvent } from "domains/topic/analytics";
import { getTopicTitle, useTopicNavigation } from "domains/topicDetail/utils";
import { TopicSearchBar } from "domains/topicSearchFilter/components/TopicSearchBar";
import { useTopicContentMeta } from "domains/topicSearchFilter/useTopicContentMeta";
import { getCanAccessTopicStakeholders } from "entities/permissions/selectors";
import { fetchStakeholderById } from "entities/stakeholders/actions";
import { createStakeholderSelector, createStakeholdersSelector } from "entities/stakeholders/selectors";
import { fetchTopic } from "entities/topics/actions";
import { createTopicSelector, getTopic } from "entities/topics/selectors";

export const TopicStakeholderPage = () => {
  const dispatch = useDispatch();
  const go = useGo();
  const { keyIssueId, stakeholderId, topicId } = useParams();
  const stakeholderAccess = useSelector(getCanAccessTopicStakeholders);
  const isFiltered = useSelector(getIsStakeholdersFiltered);
  const siteLanguage = useSelector(getSiteLanguage);
  const filterCount = useSelector(getStakeholderFilterCount);
  const { search } = useSelector(getStakeholdersSearchCriteria);
  const { hasFilters } = useTopicContentMeta("stakeholders");
  const track = useTrack();
  const translate = useTranslate();

  if (topicId === undefined) throw new Error("topicId param undefined in TopicInitiativePage");
  const selectedTopicId = keyIssueId ?? topicId;

  const stakeholdersForTopic = useSelector(createStakeholdersForTopicSelector(selectedTopicId));
  const topic = useSelector(createTopicSelector(topicId));
  const keyIssue = useSelector(getTopic(keyIssueId));
  const { path } = useTopicNavigation({ id: topicId, keyIssueId });

  const stakeholdersSelector = useMemo(
    () => createStakeholdersSelector(stakeholdersForTopic?.data ?? []),
    [stakeholdersForTopic?.data],
  );

  const stakeholders = useSelector(stakeholdersSelector);

  const id = getPageId(stakeholderId, stakeholders);

  const stakeholder = useSelector(createStakeholderSelector(id));

  const selectedTopic = keyIssueId ? keyIssue : topic;
  const hasNextPage = stakeholdersForTopic?.hasNextPage ?? true;
  const isPending = stakeholdersForTopic?.isFetching ?? false;

  useEffect(() => {
    if (id) {
      dispatch(fetchStakeholderById({ id }));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!topic || (keyIssueId && !keyIssue)) {
      return;
    }
    const event = getTopicPageEvent(topic, keyIssue, "Stakeholder List");
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keyIssue?.id, topic?.id, track]);

  useEffect(() => {
    if (!selectedTopic || !stakeholder) {
      return;
    }
    const event = getTopicRelatedItemDetailPageEvent(
      "Stakeholder detail",
      { id: stakeholder.id, name: stakeholder.full_name },
      "Stakeholder",
      selectedTopic,
    );
    track(event);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopic?.id, stakeholder?.id, track]);

  useEffect(() => {
    dispatch(
      applyStakeholdersSearchCriteria({
        topicId: selectedTopicId,
        offset: 0,
        limit: 20,
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId, siteLanguage]);

  useEffect(() => {
    if (!selectedTopic) {
      dispatch(fetchTopic({ id: topicId }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTopicId]);

  useEffect(() => {
    if (!stakeholderAccess) {
      go("/");
    }
  }, [go, stakeholderAccess]);

  const getLink = (stakeholderId: string) => `${path}/stakeholders/${stakeholderId}`;

  const loadNextPage = () => {
    dispatch(
      applyStakeholdersSearchCriteria({
        topicId: selectedTopicId,
        offset: stakeholders.length,
        limit: 20,
      }),
    );
  };

  let emptyMessage = isFiltered
    ? translate("app.discover.topic.tabs.stakeholders.no_data")
    : `${translate("app.topic.no_content")} ${translate("app.discover.topic.tabs.stakeholders.title")}.`;

  if (!topic?.id) {
    emptyMessage = translate("app.no_access.topic_content");
  }

  let list: ReactNode;

  if (stakeholders.length === 0 && !isPending) {
    list = <p>{emptyMessage}</p>;
  } else {
    list = (
      <InfinityLoader hasNextPage={hasNextPage} isPending={isPending} loadNextPage={loadNextPage}>
        {stakeholders.map((item) => (
          <StakeholderListItem
            key={item.id}
            stakeholder={item}
            isSelected={stakeholderId === item.id}
            link={getLink(item.id)}
          />
        ))}
      </InfinityLoader>
    );
  }

  const onFilterClick = () =>
    dispatch(activateModal({ id: "TopicStakeholderFilter", params: { selectedTopicId, topicId } }));

  const topicFilterButton =
    topic?.id && hasFilters ? <FilterButton onClick={onFilterClick} count={filterCount} /> : undefined;

  const onSearchHandle = (search: string) =>
    dispatch(applyStakeholdersSearchCriteria({ topicId: selectedTopicId, search }));

  const searchElement = topic?.id ? (
    <TopicSearchBar onSearch={onSearchHandle} isPending={isPending} defaultValue={search} />
  ) : undefined;

  return (
    <MasterDetailLayout
      headerElement={searchElement}
      listTitle={translate("app.discover.topic.tabs.stakeholders.title")}
      listComponent={list}
      listBackNavigation={{
        label: getTopicTitle(topic, keyIssue) ?? "",
        path,
      }}
      detailBackNavigation={{
        label: translate("app.discover.topic.tabs.stakeholders.title"),
        path: `${path}/stakeholders`,
      }}
      mainView={stakeholderId ? "right" : "left"}
      listHeaderRightElement={topicFilterButton}
    >
      <StakeholderDetail stakeholder={stakeholder} shareLink={`/topics/${topicId}/stakeholders/${id}`} />
    </MasterDetailLayout>
  );
};
